import { useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Ban, Check, Edit, Eye, Loader2, Trash2, Unlink } from 'lucide-react';
import { Status } from '@pillar-portal/database/dist/frontend';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CredentialCardView } from '@/components/secrets/CredentialCardView';
import { CredentialDeleteDialog } from '@/components/secrets/CredentialDeleteDialog';
import { CredentialDisconnectDialog } from '@/components/secrets/CredentialDisconnectDialog';
import type { SecretActionButtonsCredential } from '@/components/secrets/SecretActionButtons';
import { SecretModal } from '@/components/secrets/SecretModal';
import {
    buildCredentialUsageSelectionKey,
    toCredentialUsageReference,
} from '@/lib/secrets/credentialUsageSelection';
import { parseCredentialDeleteConflictUsages } from '@/lib/secrets/credentialDeleteConflict';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    TerminalCommandReadinessFleet,
    type TerminalCommandReadinessFleetVerdict,
} from '@/components/payments-infrastructure/TerminalCommandReadinessFleet';
import {
    useCredentialUsages,
    useDeleteSecret,
    useDisableSecret,
    useDisconnectCredentialUsages,
    useEnableSecret,
} from '@/lib/hooks/useSecrets';
import { runInBackground } from '@/lib/utils';
import { getMerchantChecklistTerminalName } from '@/lib/payments-infrastructure/displayLabels';
import { z } from 'zod';
import { organizationApi } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import type {
    ActiveRoleAssignmentResponse,
    CredentialType,
    CredentialUsageListItem,
    CredentialUsageReference,
    GetMerchantChecklistCheckResponse,
    IpxCredentialSummaryCapabilities,
    MerchantChecklistCredentialIssue,
    MerchantChecklistCheckCode,
    MerchantChecklistCheckResult,
    MerchantChecklistIssue,
    MerchantChecklistPermissionIssue,
    SecretUuidReference,
    SecretOrganizationUuidReference,
} from '@pillar-portal/api-types';
import {
    hasInternalAdminPrivileges,
    TerminalCommandReadinessResourceKind,
    ThirdPartySystemIdToName,
} from '@pillar-portal/api-types';
import { NonCoreThirdPartySystemIdSchema } from '@pillar-portal/api-types/schemas';

export const MerchantChecklistOrganizationUuidSchema: z.ZodString = z.string().uuid();

type ChecklistCredentialActionTarget = Omit<SecretActionButtonsCredential, 'capabilities'> & {
    capabilities: IpxCredentialSummaryCapabilities;
};

function ChecklistSummaryBadge({
    status,
    errorCount,
    warningCount,
}: {
    status: 'pass' | 'error' | 'warning';
    errorCount: number;
    warningCount: number;
}) {
    if (status === 'error') {
        return <Badge variant="destructive">{`${errorCount} errors`}</Badge>;
    }
    if (status === 'warning') {
        return (
            <Badge className="border-transparent bg-yellow-100 text-yellow-800">{`${warningCount} warnings`}</Badge>
        );
    }
    return (
        <Badge className="border-transparent bg-green-100 text-green-800">All checks pass</Badge>
    );
}

export function ChecklistCheckStatusBadge({ check }: { check: MerchantChecklistCheckResult }) {
    if (check.status === 'error') {
        return <Badge variant="destructive">Error</Badge>;
    }
    if (check.status === 'warning') {
        return <Badge className="border-transparent bg-yellow-100 text-yellow-800">Warning</Badge>;
    }
    return <Badge className="border-transparent bg-green-100 text-green-800">Pass</Badge>;
}

const CHECKLIST_CHECK_CODES: MerchantChecklistCheckCode[] = [
    'payment_methods_enabled',
    'permission_records_valid',
    'users_ipg_use_access',
    'users_ipw_use_access',
    'merchant_profile_has_user',
    'users_physical_terminal_access',
    'users_virtual_terminal_access',
    'users_e_invoice_terminal_access',
    'users_payment_transaction_display_access',
    'merchant_business_details_complete',
    'payment_flow_prerequisites_complete',
    'logical_terminals_configured',
    'terminal_devices_configured',
    'terminal_device_credential_link_integrity',
    'credentials_gateway_connectivity',
    'organization_credentials_decryptable',
    'role_assignment_credentials_decryptable',
    'credentials_inconsistent_by_account_id',
    'credentials_duplicated_by_account_id',
];

const CHECKLIST_LOADING_TITLES: Record<MerchantChecklistCheckCode, string> = {
    payment_methods_enabled: 'At least one payment method is enabled',
    permission_records_valid: 'No permission record validation failures',
    users_ipg_use_access:
        'Every active Merchant RoleAssignment can use at least one payment processor',
    users_ipw_use_access: 'Every active Merchant RoleAssignment can use every enabled website',
    merchant_profile_has_user: 'Merchant profile has at least one user',
    users_physical_terminal_access:
        'Every active user has access to at least one physical terminal',
    users_virtual_terminal_access: 'Every active user has access to at least one virtual terminal',
    users_e_invoice_terminal_access:
        'Every active user has access to at least one E-Invoice terminal',
    users_payment_transaction_display_access: 'Every active user can display payment transactions',
    merchant_business_details_complete:
        'Merchant Business Details are complete for receipt generation',
    payment_flow_prerequisites_complete:
        'Merchant details support payment flows and reconciliation',
    logical_terminals_configured: 'At least one active logical terminal is configured',
    terminal_devices_configured:
        'Active terminal devices include at least one physical and one virtual device',
    terminal_device_credential_link_integrity:
        'Every configured terminal device passes credential link QA checks',
    credentials_gateway_connectivity: 'Active credential gateway authentication status',
    logical_terminals_gateway_readiness: 'All linked logical terminals are ready for gateway use',
    terminal_devices_gateway_readiness: 'All linked terminal devices are ready for gateway use',
    organization_credentials_decryptable: 'All active organization credentials can be decrypted',
    role_assignment_credentials_decryptable:
        'All active role-assignment credentials can be decrypted',
    credentials_inconsistent_by_account_id:
        'No inconsistent credentials exist for the same account_id',
    credentials_duplicated_by_account_id: 'No duplicated credentials exist for the same account_id',
};

type TerminalIssueRecord = Extract<MerchantChecklistIssue, { issue_type: 'terminal' }>;
type TerminalAccessIssueRecord = Extract<MerchantChecklistIssue, { issue_type: 'terminal_access' }>;
type PhysicalTerminalAccessIssueRecord = Extract<
    TerminalAccessIssueRecord,
    { access_kind: 'physical' }
>;

export function countPhysicalTerminalAccessIssues(issues: MerchantChecklistIssue[]): {
    errorCount: number;
    warningCount: number;
} {
    const physicalTerminalAccessIssues = issues.filter(
        (issue): issue is PhysicalTerminalAccessIssueRecord =>
            issue.issue_type === 'terminal_access' && issue.access_kind === 'physical'
    );
    return {
        errorCount: physicalTerminalAccessIssues.filter((issue) => issue.severity === 'error')
            .length,
        warningCount: physicalTerminalAccessIssues.filter((issue) => issue.severity === 'warning')
            .length,
    };
}

type AggregatedTerminalQaIssue = {
    terminal: TerminalIssueRecord['terminal'];
    failedChecks: string[];
};

function formatQaCheck(label: string, pass: boolean) {
    return (
        <div className="text-xs text-muted-foreground">
            {label}{' '}
            <span className={pass ? 'text-emerald-600' : 'text-destructive'}>
                {pass ? 'PASS' : 'FAIL'}
            </span>
        </div>
    );
}

function formatTerminalAccessKindLabel(
    accessKind: TerminalAccessIssueRecord['access_kind']
): string {
    if (accessKind === 'physical') {
        return 'physical terminal';
    }
    if (accessKind === 'virtual') {
        return 'virtual terminal';
    }
    return 'E-Invoice terminal';
}

function formatTerminalAccessFailureReason(issue: TerminalAccessIssueRecord): string {
    const accessLabel: string = formatTerminalAccessKindLabel(issue.access_kind);
    if (issue.failure_reason === 'no_terminal_devices_configured') {
        return `No active ${accessLabel} devices are configured for this merchant.`;
    }
    if (issue.failure_reason === 'no_linked_credentials_configured') {
        return `Active ${accessLabel} devices exist, but none has a linked credential.`;
    }
    if (issue.failure_reason === 'missing_credential_permission') {
        return `Matching inventory exists, but this user lacks permission to use any linked credential.`;
    }
    if (issue.failure_reason === 'missing_terminal_permission') {
        return `Matching inventory exists, but this user lacks permission to use any ${accessLabel}.`;
    }
    if (issue.failure_reason === 'missing_payment_request_create_permission') {
        return (
            'Matching inventory exists, but this user lacks CREATE permission ' +
            'on payment_request scoped to the virtual terminal or wildcard.'
        );
    }
    return 'Matching inventory exists, but this user lacks both credential and terminal permissions.';
}

export function toChecklistCredentialActionTarget(
    issue: MerchantChecklistCredentialIssue
): ChecklistCredentialActionTarget | null {
    const parsedSystemId = NonCoreThirdPartySystemIdSchema.safeParse(issue.credential.system_id);
    if (!parsedSystemId.success) {
        return null;
    }
    if (issue.credential.capabilities === undefined) {
        return null;
    }
    return {
        credential_uuid: issue.credential.credential_uuid,
        account_id: issue.credential.account_id,
        system_id: parsedSystemId.data,
        credential_type: issue.credential.credential_type,
        status: issue.credential.status,
        applies_to_children: issue.credential.applies_to_children,
        capabilities: issue.credential.capabilities,
        usage_data: {
            summary: {
                logical_terminal_count: 0,
                terminal_device_count: 0,
                total_count: 0,
                has_usages: false,
            },
            usages: [],
        },
    };
}

export function toChecklistSecretIdentityInput(
    credential: Pick<SecretActionButtonsCredential, 'credential_uuid'>
): SecretUuidReference {
    if (credential.credential_uuid === undefined) {
        throw new Error('Missing credential_uuid');
    }
    return {
        credential_uuid: credential.credential_uuid,
    };
}

function getExpectedCredentialType(isVirtual: boolean | null): CredentialType | null {
    if (isVirtual === null) {
        return null;
    }
    return isVirtual ? 'VIRTUAL_TERMINAL' : 'PHYSICAL_TERMINAL';
}

export function getTerminalIssueGroupKey(issue: TerminalIssueRecord): string {
    if (issue.terminal.terminal_device_uuid !== null) {
        return `td:${issue.terminal.terminal_device_uuid}`;
    }
    return `lt:${issue.terminal.logical_terminal_uuid}:${issue.terminal.processor_device_id ?? 'unknown'}`;
}

export function aggregateTerminalQaIssues(
    issues: MerchantChecklistIssue[]
): AggregatedTerminalQaIssue[] {
    const terminalIssues: TerminalIssueRecord[] = issues.filter(
        (issue): issue is TerminalIssueRecord => issue.issue_type === 'terminal'
    );
    const aggregationByTerminalKey = new Map<string, AggregatedTerminalQaIssue>();
    terminalIssues.forEach((terminalIssue) => {
        const terminalKey: string = getTerminalIssueGroupKey(terminalIssue);
        const existingAggregation = aggregationByTerminalKey.get(terminalKey);
        if (existingAggregation === undefined) {
            aggregationByTerminalKey.set(terminalKey, {
                terminal: terminalIssue.terminal,
                failedChecks: [terminalIssue.message],
            });
            return;
        }
        existingAggregation.failedChecks.push(terminalIssue.message);
    });
    return Array.from(aggregationByTerminalKey.values());
}

type TerminalDeviceQaCardProps = {
    terminal: TerminalIssueRecord['terminal'];
    failedChecks: string[];
    onOpenTerminalResource: (terminal: TerminalIssueRecord['terminal']) => void;
    onOpenCredentialLink: (credentialUuid?: string | null) => void;
};

function TerminalDeviceQaCard({
    terminal,
    failedChecks,
    onOpenTerminalResource,
    onOpenCredentialLink,
}: TerminalDeviceQaCardProps) {
    const canDisplayTerminal: boolean = terminal.capabilities?.can_display ?? false;
    const canDisplayCredential: boolean = terminal.capabilities?.can_display_credential ?? false;
    const hasCredentialLink: boolean = terminal.credential_uuid !== null;
    const hasTerminalDeviceCredentialType: boolean =
        terminal.terminal_device_credential_type !== null;
    const terminalDeviceCredentialTypeMatchesLinkedCredentialType: boolean =
        terminal.linked_credential_type !== null &&
        terminal.terminal_device_credential_type !== null &&
        terminal.linked_credential_type === terminal.terminal_device_credential_type;
    const expectedCredentialType: CredentialType | null = getExpectedCredentialType(
        terminal.is_virtual
    );
    const terminalVirtualityMatchesLinkedCredentialType: boolean =
        expectedCredentialType !== null &&
        terminal.linked_credential_type !== null &&
        expectedCredentialType === terminal.linked_credential_type;
    return (
        <div className="rounded border p-3 space-y-2">
            <div className="font-medium">
                {getMerchantChecklistTerminalName(terminal) ??
                    `Logical terminal ${terminal.processor_tid}` +
                        (terminal.terminal_device_uuid !== null
                            ? ` / device ${terminal.processor_device_id ?? '(unknown)'}`
                            : '')}
            </div>
            <div className="text-xs text-destructive">{`${failedChecks.length} QA check(s) failed`}</div>
            <div className="space-y-1">
                {formatQaCheck('QA: terminal credential link is present:', hasCredentialLink)}
                {formatQaCheck(
                    'QA: terminal credential type is set:',
                    hasTerminalDeviceCredentialType
                )}
                {formatQaCheck(
                    'QA: terminal credential type matches linked credential:',
                    terminalDeviceCredentialTypeMatchesLinkedCredentialType
                )}
                {formatQaCheck(
                    'QA: is_virtual matches linked credential type:',
                    terminalVirtualityMatchesLinkedCredentialType
                )}
            </div>
            <div className="text-xs text-muted-foreground">
                terminal credential_type=
                {terminal.terminal_device_credential_type ?? 'null'}, linked credential_type=
                {terminal.linked_credential_type ?? 'null'}, is_virtual=
                {terminal.is_virtual === null ? 'null' : String(terminal.is_virtual)}
            </div>
            <div className="flex justify-end gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canDisplayTerminal}
                    title={
                        canDisplayTerminal
                            ? 'View terminal details'
                            : 'You do not have permission to view this terminal.'
                    }
                    onClick={() => {
                        onOpenTerminalResource(terminal);
                    }}
                >
                    {terminal.terminal_device_uuid !== null
                        ? 'View Terminal Device'
                        : 'View Logical Terminal'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canDisplayCredential}
                    title={
                        canDisplayCredential
                            ? 'View credential details'
                            : 'You do not have permission to view this credential.'
                    }
                    onClick={() => {
                        onOpenCredentialLink(terminal.credential_uuid);
                    }}
                >
                    {terminal.credential_uuid === null ? 'View Credentials' : 'View Credential'}
                </Button>
            </div>
        </div>
    );
}

type ChecklistIssueCardProps = {
    issue: MerchantChecklistIssue;
    checkCode?: MerchantChecklistCheckCode;
    organizationUuid: string;
    onViewUser: (userUuid: string) => void;
    onReviewPermissionIssue: (issue: MerchantChecklistPermissionIssue) => void;
    onViewCredential: (credential: SecretActionButtonsCredential) => void;
    onEditCredential: (credential: SecretActionButtonsCredential) => void;
    onDisableCredential: (credential: SecretActionButtonsCredential) => void;
    onEnableCredential: (credential: SecretActionButtonsCredential) => void;
    onDeleteCredential: (credential: SecretActionButtonsCredential) => void;
    onDisconnectCredential: (credential: SecretActionButtonsCredential) => void;
    onOpenTerminalResource: (terminal: TerminalIssueRecord['terminal']) => void;
    onOpenCredentialLink: (credentialUuid?: string | null) => void;
};

export function buildChecklistUserHref(userUuid: string): string {
    return `/users/${userUuid}`;
}

export function buildMerchantChecklistPermissionAuditHref(
    organizationUuid: string,
    roleAssignmentUuid: string
): string {
    return `/organizations/${organizationUuid}/permissions-audit?role_assignment_uuid=${roleAssignmentUuid}`;
}

export function buildMerchantChecklistOrganizationAuditHref(organizationUuid: string): string {
    return `/organizations/${organizationUuid}/permissions-audit`;
}

function formatPermissionFindingCodeLabel(code: string): string {
    return code
        .toLowerCase()
        .split('_')
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
}

type ChecklistHeaderActionProps = {
    check: MerchantChecklistCheckResult;
    onOpenMerchantBusinessDetails: () => void;
    onOpenMerchantConfiguration: () => void;
    onOpenOrganizationUsers: () => void;
    onOpenPermissionsAudit: () => void;
};

export function ChecklistTopLevelAction({
    check,
    onOpenMerchantBusinessDetails,
    onOpenMerchantConfiguration,
    onOpenOrganizationUsers,
    onOpenPermissionsAudit,
}: ChecklistHeaderActionProps) {
    if (check.status === 'pass') {
        return null;
    }
    if (
        check.code === 'merchant_business_details_complete' ||
        check.code === 'payment_flow_prerequisites_complete'
    ) {
        return (
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOpenMerchantBusinessDetails}
            >
                View
            </Button>
        );
    }
    if (check.code === 'payment_methods_enabled') {
        return (
            <Button type="button" variant="outline" size="sm" onClick={onOpenMerchantConfiguration}>
                View
            </Button>
        );
    }
    if (check.code === 'merchant_profile_has_user') {
        return (
            <Button type="button" variant="outline" size="sm" onClick={onOpenOrganizationUsers}>
                View
            </Button>
        );
    }
    if (
        check.code === 'permission_records_valid' ||
        check.code === 'users_ipg_use_access' ||
        check.code === 'users_ipw_use_access'
    ) {
        return (
            <Button type="button" variant="outline" size="sm" onClick={onOpenPermissionsAudit}>
                View
            </Button>
        );
    }
    return null;
}

type ChecklistQueryErrorCardProps = {
    checkCode: MerchantChecklistCheckCode;
    errorMessage: string;
    onOpenMerchantBusinessDetails: () => void;
    onOpenMerchantConfiguration: () => void;
    onOpenOrganizationUsers: () => void;
    onOpenPermissionsAudit: () => void;
};

export function ChecklistQueryErrorCard({
    checkCode,
    errorMessage,
    onOpenMerchantBusinessDetails,
    onOpenMerchantConfiguration,
    onOpenOrganizationUsers,
    onOpenPermissionsAudit,
}: ChecklistQueryErrorCardProps) {
    const actionCheck: MerchantChecklistCheckResult = {
        code: checkCode,
        title: CHECKLIST_LOADING_TITLES[checkCode],
        severity: 'error',
        status: 'error',
        details: [],
        issues: [],
    };
    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">{CHECKLIST_LOADING_TITLES[checkCode]}</CardTitle>
                <Badge variant="destructive">Error</Badge>
            </CardHeader>
            <CardContent>
                <div className="flex items-start justify-between gap-3">
                    <div className="text-sm text-destructive">{errorMessage}</div>
                    <ChecklistTopLevelAction
                        check={actionCheck}
                        onOpenMerchantBusinessDetails={onOpenMerchantBusinessDetails}
                        onOpenMerchantConfiguration={onOpenMerchantConfiguration}
                        onOpenOrganizationUsers={onOpenOrganizationUsers}
                        onOpenPermissionsAudit={onOpenPermissionsAudit}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

export function parseMerchantChecklistOrganizationUuid(
    rawOrganizationUuid: string | undefined
): string {
    const parseResult = MerchantChecklistOrganizationUuidSchema.safeParse(rawOrganizationUuid);
    if (!parseResult.success) {
        throw new Error(`Invalid organization UUID: ${rawOrganizationUuid}`);
    }
    return parseResult.data;
}

export function buildMerchantChecklistBackHref(organizationUuid: string): string {
    return `/organizations/${organizationUuid}?view=merchant`;
}

export function buildMerchantBusinessDetailsHref(organizationUuid: string): string {
    return `/organizations/${organizationUuid}?view=merchant`;
}

export function buildMerchantChecklistMerchantConfigurationHref(organizationUuid: string): string {
    return `/organizations/${organizationUuid}?view=merchant&section=merchant-configuration`;
}

export function buildMerchantChecklistOrganizationUsersHref(organizationUuid: string): string {
    return `/organizations/${organizationUuid}?view=merchant&section=users`;
}

export function buildMerchantChecklistCredentialsHref(
    organizationUuid: string,
    credentialUuid?: string | null
): string {
    const searchParams = new URLSearchParams({
        view: 'merchant',
        section: 'credentials',
    });
    if (credentialUuid !== undefined && credentialUuid !== null) {
        searchParams.set('credential_uuid', credentialUuid);
    }
    return `/organizations/${organizationUuid}?${searchParams.toString()}`;
}

export function buildMerchantChecklistLogicalTerminalHref(logicalTerminalUuid: string): string {
    return `/payments-infrastructure/logical-terminals/${logicalTerminalUuid}`;
}

export function buildMerchantChecklistTerminalDeviceHref(terminalDeviceUuid: string): string {
    return `/payments-infrastructure/terminal-devices/${terminalDeviceUuid}`;
}

export function buildMerchantChecklistTerminalResourceHref(
    terminal: TerminalIssueRecord['terminal']
): string {
    if (terminal.terminal_device_uuid !== null) {
        return buildMerchantChecklistTerminalDeviceHref(terminal.terminal_device_uuid);
    }
    return buildMerchantChecklistLogicalTerminalHref(terminal.logical_terminal_uuid);
}

function ChecklistCredentialActionButtons({
    credential,
    onView,
    onEdit,
    onDisable,
    onEnable,
    onDelete,
    onDisconnect,
}: {
    credential: ChecklistCredentialActionTarget;
    onView: () => void;
    onEdit: () => void;
    onDisable: () => void;
    onEnable: () => void;
    onDelete: () => void;
    onDisconnect: () => void;
}) {
    const isActive: boolean = credential.status === Status.ACTIVE;
    const isDeleted: boolean = credential.status === Status.DELETED;
    const canEnable: boolean = isDeleted
        ? credential.capabilities.can_restore
        : credential.capabilities.can_enable;
    return (
        <div className="flex gap-2 ml-4">
            <Button
                variant="outline"
                size="sm"
                disabled={!credential.capabilities.can_display}
                onClick={onView}
                title={
                    credential.capabilities.can_display
                        ? 'View secret details'
                        : 'You do not have permission to view this secret.'
                }
            >
                <Eye className="h-4 w-4" />
            </Button>
            <Button
                variant="outline"
                size="sm"
                disabled={!credential.capabilities.can_edit}
                onClick={onEdit}
                title={
                    credential.capabilities.can_edit
                        ? 'Edit secret'
                        : 'You do not have permission to edit this secret.'
                }
            >
                <Edit className="h-4 w-4" />
            </Button>
            {isActive ? (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!credential.capabilities.can_disable}
                    onClick={onDisable}
                    title={
                        credential.capabilities.can_disable
                            ? 'Disable secret'
                            : 'You do not have permission to disable this secret.'
                    }
                >
                    <Ban className="h-4 w-4" />
                </Button>
            ) : (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!canEnable}
                    onClick={onEnable}
                    title={
                        canEnable
                            ? isDeleted
                                ? 'Undelete secret'
                                : 'Enable secret'
                            : isDeleted
                              ? 'You do not have permission to undelete this secret.'
                              : 'You do not have permission to enable this secret.'
                    }
                    className="text-green-700 hover:text-green-800"
                >
                    <Check className="h-4 w-4" />
                </Button>
            )}
            <Button
                variant="outline"
                size="sm"
                disabled={!credential.capabilities.can_delete}
                onClick={onDelete}
                title={
                    credential.capabilities.can_delete
                        ? 'Delete secret'
                        : 'You do not have permission to delete this secret.'
                }
                className="text-red-600 hover:text-red-700"
            >
                <Trash2 className="h-4 w-4" />
            </Button>
            <Button
                variant="outline"
                size="sm"
                disabled={!credential.capabilities.can_edit}
                onClick={onDisconnect}
                title={
                    credential.capabilities.can_edit
                        ? 'Disconnect this credential from selected usages'
                        : 'You do not have permission to disconnect this credential.'
                }
            >
                <Unlink className="h-4 w-4" />
            </Button>
        </div>
    );
}

export function ChecklistIssueCard({
    issue,
    checkCode,
    organizationUuid,
    onViewUser,
    onReviewPermissionIssue,
    onViewCredential,
    onEditCredential,
    onDisableCredential,
    onEnableCredential,
    onDeleteCredential,
    onDisconnectCredential,
    onOpenTerminalResource,
    onOpenCredentialLink,
}: ChecklistIssueCardProps) {
    if (issue.issue_type === 'credential') {
        const credentialActionTarget = toChecklistCredentialActionTarget(issue);
        if (credentialActionTarget === null) {
            return (
                <div className="border rounded-lg p-4 bg-muted/20">
                    <div className="font-medium">{issue.credential.account_id}</div>
                    <div className="text-sm text-muted-foreground">{issue.message}</div>
                </div>
            );
        }
        return (
            <CredentialCardView
                credential={{
                    ...issue.credential,
                    system_id: credentialActionTarget.system_id,
                }}
                showPublicFields={true}
                actions={
                    <ChecklistCredentialActionButtons
                        credential={credentialActionTarget}
                        onView={() => onViewCredential(credentialActionTarget)}
                        onEdit={() => onEditCredential(credentialActionTarget)}
                        onDisable={() => onDisableCredential(credentialActionTarget)}
                        onEnable={() => onEnableCredential(credentialActionTarget)}
                        onDelete={() => onDeleteCredential(credentialActionTarget)}
                        onDisconnect={() => onDisconnectCredential(credentialActionTarget)}
                    />
                }
            >
                <div className="text-sm text-muted-foreground">{issue.message}</div>
            </CredentialCardView>
        );
    }

    if (issue.issue_type === 'permission') {
        const canDisplayPermission: boolean = issue.capabilities?.can_display ?? false;
        return (
            <div className="border rounded-lg p-4 bg-muted/20">
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                        <div className="font-medium">{issue.user.display_name}</div>
                        <div className="text-sm text-muted-foreground">{issue.message}</div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatPermissionFindingCodeLabel(issue.finding_code)}</span>
                            {issue.permissions_bundle_name !== null ? (
                                <span>{issue.permissions_bundle_name}</span>
                            ) : null}
                            {issue.permission_index !== null ? (
                                <span>{`Permission #${issue.permission_index + 1}`}</span>
                            ) : null}
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!canDisplayPermission}
                        onClick={() => onReviewPermissionIssue(issue)}
                        title={
                            canDisplayPermission
                                ? buildMerchantChecklistPermissionAuditHref(
                                      organizationUuid,
                                      issue.role_assignment_uuid
                                  )
                                : 'You do not have permission to view this permission record.'
                        }
                    >
                        View
                    </Button>
                </div>
            </div>
        );
    }

    if (issue.issue_type === 'ipg_access' || issue.issue_type === 'ipw_access') {
        const canDisplayUser: boolean = issue.user.capabilities?.can_display ?? false;
        return (
            <div className="border rounded-lg p-4 bg-muted/20">
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                        <div className="font-medium">{issue.user.display_name}</div>
                        <div className="text-sm text-muted-foreground">{issue.message}</div>
                        <div className="space-y-1 text-xs text-muted-foreground">
                            <div>{`RoleAssignment ${issue.role_assignment_uuid}`}</div>
                            <div>{`Organization ${issue.organization_uuid}`}</div>
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!canDisplayUser}
                        onClick={() => onViewUser(issue.user.user_uuid)}
                        title={
                            canDisplayUser
                                ? 'View user details'
                                : 'You do not have permission to view this user.'
                        }
                    >
                        View
                    </Button>
                </div>
            </div>
        );
    }

    if (issue.issue_type === 'terminal') {
        const isTerminalResourceSummaryIssue: boolean =
            checkCode === 'logical_terminals_configured' ||
            checkCode === 'terminal_devices_configured' ||
            checkCode === 'logical_terminals_gateway_readiness' ||
            checkCode === 'terminal_devices_gateway_readiness';
        if (isTerminalResourceSummaryIssue) {
            const canDisplayTerminal: boolean = issue.terminal.capabilities?.can_display ?? false;
            return (
                <div className="border rounded-lg p-4 bg-muted/20">
                    <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                            <div className="font-medium">
                                {getMerchantChecklistTerminalName(issue.terminal) ??
                                    (issue.terminal.terminal_device_uuid !== null
                                        ? `Terminal device ${issue.terminal.processor_device_id ?? '(unknown)'}`
                                        : `Logical terminal ${issue.terminal.processor_tid}`)}
                            </div>
                            <div className="text-sm text-muted-foreground">{issue.message}</div>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!canDisplayTerminal}
                            onClick={() => onOpenTerminalResource(issue.terminal)}
                            title={
                                canDisplayTerminal
                                    ? buildMerchantChecklistTerminalResourceHref(issue.terminal)
                                    : 'You do not have permission to view this terminal.'
                            }
                        >
                            View
                        </Button>
                    </div>
                </div>
            );
        }
        return (
            <TerminalDeviceQaCard
                terminal={issue.terminal}
                failedChecks={[issue.message]}
                onOpenTerminalResource={onOpenTerminalResource}
                onOpenCredentialLink={onOpenCredentialLink}
            />
        );
    }

    if (issue.issue_type === 'terminal_access') {
        const canDisplayUser: boolean = issue.user.capabilities?.can_display ?? false;
        return (
            <div className="border rounded-lg p-4 bg-muted/20">
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <div className="font-medium">{issue.user.display_name}</div>
                            {issue.access_kind === 'physical' ? (
                                <Badge
                                    variant={issue.severity === 'error' ? 'destructive' : 'outline'}
                                    className={
                                        issue.severity === 'warning'
                                            ? 'border-yellow-300 bg-yellow-100 text-yellow-800'
                                            : undefined
                                    }
                                >
                                    {issue.severity === 'error' ? 'Error' : 'Warning'}
                                </Badge>
                            ) : null}
                        </div>
                        <div className="text-sm text-muted-foreground">{issue.message}</div>
                        {issue.access_kind === 'physical' ? (
                            <div className="space-y-1 text-xs text-muted-foreground">
                                <div>{`RoleAssignment ${issue.role_assignment_uuid}`}</div>
                                <div>{`Organization ${issue.organization_uuid}`}</div>
                            </div>
                        ) : null}
                        <div className="text-xs text-muted-foreground">
                            {formatTerminalAccessFailureReason(issue)}
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!canDisplayUser}
                        onClick={() => onViewUser(issue.user.user_uuid)}
                        title={
                            canDisplayUser
                                ? 'View user details'
                                : 'You do not have permission to view this user.'
                        }
                    >
                        View
                    </Button>
                </div>
            </div>
        );
    }

    const canDisplayUser: boolean = issue.user.capabilities?.can_display ?? false;
    return (
        <div className="border rounded-lg p-4 bg-muted/20">
            <div className="flex items-center justify-between gap-3">
                <div className="font-medium">{issue.user.display_name}</div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canDisplayUser}
                    onClick={() => onViewUser(issue.user.user_uuid)}
                    title={
                        canDisplayUser
                            ? 'View user details'
                            : 'You do not have permission to view this user.'
                    }
                >
                    View
                </Button>
            </div>
        </div>
    );
}

export default function MerchantChecklistPage() {
    const navigate = useNavigate();
    const params = useParams();
    const organizationUuid: string = parseMerchantChecklistOrganizationUuid(params.id);
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
    const [selectedCredential, setSelectedCredential] =
        useState<SecretActionButtonsCredential | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [credentialToDelete, setCredentialToDelete] =
        useState<SecretActionButtonsCredential | null>(null);
    const [selectedDeleteUsageKeys, setSelectedDeleteUsageKeys] = useState<string[]>([]);
    const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
    const [credentialToDisconnect, setCredentialToDisconnect] =
        useState<SecretActionButtonsCredential | null>(null);
    const [selectedUsageKeys, setSelectedUsageKeys] = useState<string[]>([]);
    const [deleteConflictUsages, setDeleteConflictUsages] = useState<
        CredentialUsageListItem[] | null
    >(null);
    const [terminalCommandReadinessFleetVerdicts, setTerminalCommandReadinessFleetVerdicts] =
        useState<
            Partial<
                Record<TerminalCommandReadinessResourceKind, TerminalCommandReadinessFleetVerdict>
            >
        >({});
    const hasUnsavedChanges: boolean = modalOpen && modalMode === 'edit';
    useUnsavedChangesRegistration(hasUnsavedChanges);

    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await sessionSensitiveAuthApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });
    const canViewChecklist: boolean = hasInternalAdminPrivileges(
        getActiveRoleAssignmentOperationalResponse(activeRoleAssignmentQuery.data)
    );

    const deleteMutation = useDeleteSecret();
    const disableMutation = useDisableSecret();
    const enableMutation = useEnableSecret();
    const disconnectMutation = useDisconnectCredentialUsages();
    const organizationReference: SecretOrganizationUuidReference = {
        organization_uuid: organizationUuid,
    };
    const credentialIdentityForDisconnectDialog: SecretUuidReference | undefined =
        disconnectDialogOpen && credentialToDisconnect !== null
            ? toChecklistSecretIdentityInput(credentialToDisconnect)
            : undefined;
    const credentialIdentityForDeleteDialog: SecretUuidReference | undefined =
        deleteConfirmOpen && credentialToDelete !== null
            ? toChecklistSecretIdentityInput(credentialToDelete)
            : undefined;
    const {
        data: credentialUsagesResponse,
        isLoading: isCredentialUsagesLoading,
        error: credentialUsagesError,
    } = useCredentialUsages(credentialIdentityForDisconnectDialog);
    const {
        data: deleteCredentialUsagesResponse,
        isLoading: isDeleteCredentialUsagesLoading,
        error: deleteCredentialUsagesError,
    } = useCredentialUsages(credentialIdentityForDeleteDialog);

    const checklistQueries = useQueries({
        queries: CHECKLIST_CHECK_CODES.map((checkCode) => ({
            queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'organization',
                organizationUuid,
                'merchant-checklist',
                checkCode,
            ]),
            queryFn: async ({ signal }): Promise<GetMerchantChecklistCheckResponse> =>
                await organizationApi.getMerchantChecklistCheck(organizationUuid, checkCode, {
                    signal,
                }),
            enabled: canViewChecklist,
            staleTime: 0,
            refetchOnMount: 'always',
            gcTime: 1800000,
        })),
    });
    const checklistChecks: MerchantChecklistCheckResult[] = checklistQueries.flatMap(
        (queryResult) => (queryResult.data !== undefined ? [queryResult.data.check] : [])
    );
    const terminalCommandReadinessFleetErrorCount: number = [
        terminalCommandReadinessFleetVerdicts[
            TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL
        ],
        terminalCommandReadinessFleetVerdicts[TerminalCommandReadinessResourceKind.TERMINAL_DEVICE],
    ].filter((verdict) => verdict?.status === 'error').length;
    const checklistErrorCount: number =
        checklistChecks.filter((check) => check.status === 'error').length +
        terminalCommandReadinessFleetErrorCount;
    const checklistWarningCount: number = checklistChecks.filter(
        (check) => check.status === 'warning'
    ).length;
    const checklistSummaryStatus: 'pass' | 'error' | 'warning' =
        checklistErrorCount > 0 ? 'error' : checklistWarningCount > 0 ? 'warning' : 'pass';
    const hasChecklistPendingQuery: boolean = checklistQueries.some(
        (queryResult) => queryResult.isPending || queryResult.isFetching
    );
    const handleTerminalCommandReadinessFleetVerdictChange = useCallback(
        (
            resourceKind: TerminalCommandReadinessResourceKind,
            verdict: TerminalCommandReadinessFleetVerdict | null
        ): void => {
            setTerminalCommandReadinessFleetVerdicts((currentVerdicts) => {
                if (verdict === null) {
                    const nextVerdicts = { ...currentVerdicts };
                    delete nextVerdicts[resourceKind];
                    return nextVerdicts;
                }
                return { ...currentVerdicts, [resourceKind]: verdict };
            });
        },
        []
    );

    if (!activeRoleAssignmentQuery.isPending && !canViewChecklist) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleViewCredential = (credential: SecretActionButtonsCredential): void => {
        setSelectedCredential(credential);
        setModalMode('view');
        setModalOpen(true);
    };
    const handleViewUser = (userUuid: string): void => {
        Promise.resolve(navigate(buildChecklistUserHref(userUuid))).catch(
            (navigationError: unknown) => {
                console.error('Failed to navigate to user detail page', navigationError);
            }
        );
    };
    const handleOpenMerchantBusinessDetails = (): void => {
        runInBackground(
            navigate(buildMerchantBusinessDetailsHref(organizationUuid)),
            (_error: unknown) => undefined
        );
    };
    const handleOpenMerchantConfiguration = (): void => {
        runInBackground(
            navigate(buildMerchantChecklistMerchantConfigurationHref(organizationUuid)),
            (_error: unknown) => undefined
        );
    };
    const handleOpenOrganizationUsers = (): void => {
        runInBackground(
            navigate(buildMerchantChecklistOrganizationUsersHref(organizationUuid)),
            (_error: unknown) => undefined
        );
    };
    const handleOpenPermissionsAudit = (): void => {
        runInBackground(
            navigate(buildMerchantChecklistOrganizationAuditHref(organizationUuid)),
            (_error: unknown) => undefined
        );
    };
    const handleOpenCredentialLink = (credentialUuid?: string | null): void => {
        runInBackground(
            navigate(buildMerchantChecklistCredentialsHref(organizationUuid, credentialUuid)),
            (_error: unknown) => undefined
        );
    };
    const handleOpenTerminalResource = (terminal: TerminalIssueRecord['terminal']): void => {
        runInBackground(
            navigate(buildMerchantChecklistTerminalResourceHref(terminal)),
            (_error: unknown) => undefined
        );
    };
    const handleReviewPermissionIssue = (issue: MerchantChecklistPermissionIssue): void => {
        Promise.resolve(
            navigate(
                buildMerchantChecklistPermissionAuditHref(
                    organizationUuid,
                    issue.role_assignment_uuid
                )
            )
        ).catch((navigationError: unknown) => {
            console.error('Failed to navigate to permissions audit page', navigationError);
        });
    };
    const handleEditCredential = (credential: SecretActionButtonsCredential): void => {
        setSelectedCredential(credential);
        setModalMode('edit');
        setModalOpen(true);
    };
    const handleDisableCredential = (credential: SecretActionButtonsCredential): void => {
        disableMutation.mutate({
            secret: toChecklistSecretIdentityInput(credential),
            organization: organizationReference,
        });
    };
    const handleEnableCredential = (credential: SecretActionButtonsCredential): void => {
        enableMutation.mutate({
            secret: toChecklistSecretIdentityInput(credential),
            organization: organizationReference,
        });
    };
    const handleDeleteCredential = (credential: SecretActionButtonsCredential): void => {
        setCredentialToDelete(credential);
        setDeleteConflictUsages(null);
        setSelectedDeleteUsageKeys([]);
        setDeleteConfirmOpen(true);
    };
    const handleConfirmDeleteCredential = (): void => {
        if (credentialToDelete === null) {
            return;
        }
        deleteMutation.mutate(
            {
                secret: toChecklistSecretIdentityInput(credentialToDelete),
                organization: organizationReference,
            },
            {
                onSuccess: () => {
                    setDeleteConfirmOpen(false);
                    setCredentialToDelete(null);
                    setDeleteConflictUsages(null);
                    setSelectedDeleteUsageKeys([]);
                },
                onError: (deleteError: unknown) => {
                    const conflictUsages: CredentialUsageListItem[] | null =
                        parseCredentialDeleteConflictUsages(deleteError);
                    if (conflictUsages !== null) {
                        setDeleteConflictUsages(conflictUsages);
                    }
                },
            }
        );
    };
    const handleDisconnectCredential = (credential: SecretActionButtonsCredential): void => {
        setCredentialToDisconnect(credential);
        setSelectedUsageKeys([]);
        setDisconnectDialogOpen(true);
    };
    const handleToggleUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ): void => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedUsageKeys((previousSelectionKeys) =>
                previousSelectionKeys.includes(usageSelectionKey)
                    ? previousSelectionKeys
                    : [...previousSelectionKeys, usageSelectionKey]
            );
            return;
        }
        setSelectedUsageKeys((previousSelectionKeys) =>
            previousSelectionKeys.filter(
                (previousSelectionKey) => previousSelectionKey !== usageSelectionKey
            )
        );
    };
    const handleConfirmDisconnect = (): void => {
        if (credentialToDisconnect === null) {
            return;
        }
        const availableUsages: CredentialUsageListItem[] = credentialUsagesResponse?.usages ?? [];
        const selectedUsages: CredentialUsageReference[] = availableUsages
            .filter((usage) => selectedUsageKeys.includes(buildCredentialUsageSelectionKey(usage)))
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        disconnectMutation.mutate(
            {
                secret: toChecklistSecretIdentityInput(credentialToDisconnect),
                organization: organizationReference,
                usages: selectedUsages,
            },
            {
                onSuccess: () => {
                    setDisconnectDialogOpen(false);
                    setCredentialToDisconnect(null);
                    setSelectedUsageKeys([]);
                },
            }
        );
    };

    const deleteCredentialUsages: CredentialUsageListItem[] =
        deleteConflictUsages ?? deleteCredentialUsagesResponse?.usages ?? [];
    const getSystemName = (systemId: SecretActionButtonsCredential['system_id']): string => {
        const systemName = ThirdPartySystemIdToName[systemId];
        if (systemName !== undefined) {
            return systemName
                .toLowerCase()
                .split('_')
                .map((segment) => {
                    if (segment.length <= 2) {
                        return segment.toUpperCase();
                    }
                    return segment.charAt(0).toUpperCase() + segment.slice(1);
                })
                .join(' ');
        }
        return `System ${systemId}`;
    };

    const handleToggleDeleteUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ): void => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedDeleteUsageKeys((previousSelectionKeys) =>
                previousSelectionKeys.includes(usageSelectionKey)
                    ? previousSelectionKeys
                    : [...previousSelectionKeys, usageSelectionKey]
            );
            return;
        }
        setSelectedDeleteUsageKeys((previousSelectionKeys) =>
            previousSelectionKeys.filter(
                (previousSelectionKey) => previousSelectionKey !== usageSelectionKey
            )
        );
    };

    const handleToggleDeleteSelectAll = (checked: boolean): void => {
        if (!checked) {
            setSelectedDeleteUsageKeys([]);
            return;
        }
        setSelectedDeleteUsageKeys(
            deleteCredentialUsages.map((usage) => buildCredentialUsageSelectionKey(usage))
        );
    };

    const handleRemoveDeleteUsages = (): void => {
        if (credentialToDelete === null) {
            return;
        }
        const selectedUsages: CredentialUsageReference[] = deleteCredentialUsages
            .filter((usage) =>
                selectedDeleteUsageKeys.includes(buildCredentialUsageSelectionKey(usage))
            )
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        disconnectMutation.mutate(
            {
                secret: toChecklistSecretIdentityInput(credentialToDelete),
                organization: organizationReference,
                usages: selectedUsages,
            },
            {
                onSuccess: () => {
                    setSelectedDeleteUsageKeys([]);
                    setDeleteConflictUsages(null);
                },
            }
        );
    };

    return (
        <div
            id="merchant-configuration-checklist"
            data-organization-uuid={organizationUuid}
            className="space-y-6"
        >
            <BackButton
                href={buildMerchantChecklistBackHref(organizationUuid)}
                tooltip="Back to Organization Details"
            />
            <div className="space-y-2">
                <h1 className="text-3xl font-bold">Merchant Configuration Checklist</h1>
                {!hasChecklistPendingQuery ? (
                    <div
                        id="merchant-checklist-summary"
                        data-checklist-status={checklistSummaryStatus}
                        data-error-count={checklistErrorCount}
                        data-warning-count={checklistWarningCount}
                    >
                        <ChecklistSummaryBadge
                            status={checklistSummaryStatus}
                            errorCount={checklistErrorCount}
                            warningCount={checklistWarningCount}
                        />
                    </div>
                ) : null}
            </div>
            {hasChecklistPendingQuery ? (
                <div className="text-sm text-muted-foreground">Running checks...</div>
            ) : null}
            {deleteMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {deleteMutation.error instanceof Error
                            ? deleteMutation.error.message
                            : 'Failed to delete credential'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {disableMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {disableMutation.error instanceof Error
                            ? disableMutation.error.message
                            : 'Failed to disable credential'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {enableMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {enableMutation.error instanceof Error
                            ? enableMutation.error.message
                            : 'Failed to enable credential'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {disconnectMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {disconnectMutation.error instanceof Error
                            ? disconnectMutation.error.message
                            : 'Failed to disconnect credential usages'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {CHECKLIST_CHECK_CODES.map((checkCode, checkIndex) => {
                const checklistQuery = checklistQueries[checkIndex];
                if (checklistQuery === undefined) {
                    return null;
                }
                if (checklistQuery.isPending || checklistQuery.isFetching) {
                    return (
                        <Card
                            key={checkCode}
                            data-merchant-check-code={checkCode}
                            data-merchant-check-status="pending"
                        >
                            <CardHeader className="flex flex-row items-center justify-between space-y-0">
                                <CardTitle className="text-base">
                                    {CHECKLIST_LOADING_TITLES[checkCode]}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Running check...
                                </div>
                            </CardContent>
                        </Card>
                    );
                }
                if (checklistQuery.error !== null) {
                    return (
                        <div
                            key={checkCode}
                            data-merchant-check-code={checkCode}
                            data-merchant-check-status="query-error"
                        >
                            <ChecklistQueryErrorCard
                                checkCode={checkCode}
                                errorMessage={
                                    checklistQuery.error instanceof Error
                                        ? checklistQuery.error.message
                                        : 'Failed to run this check'
                                }
                                onOpenMerchantBusinessDetails={handleOpenMerchantBusinessDetails}
                                onOpenMerchantConfiguration={handleOpenMerchantConfiguration}
                                onOpenOrganizationUsers={handleOpenOrganizationUsers}
                                onOpenPermissionsAudit={handleOpenPermissionsAudit}
                            />
                        </div>
                    );
                }
                if (checklistQuery.data === undefined) {
                    return null;
                }
                const check: MerchantChecklistCheckResult = checklistQuery.data.check;
                const isTerminalDeviceCredentialLinkIntegrityCheck: boolean =
                    check.code === 'terminal_device_credential_link_integrity';
                const aggregatedTerminalQaIssues: AggregatedTerminalQaIssue[] =
                    isTerminalDeviceCredentialLinkIntegrityCheck
                        ? aggregateTerminalQaIssues(check.issues)
                        : [];
                const nonTerminalIssues: MerchantChecklistIssue[] =
                    isTerminalDeviceCredentialLinkIntegrityCheck
                        ? check.issues.filter((issue) => issue.issue_type !== 'terminal')
                        : check.issues;
                const physicalTerminalIssueCounts =
                    check.code === 'users_physical_terminal_access'
                        ? countPhysicalTerminalAccessIssues(check.issues)
                        : null;
                return (
                    <Card
                        key={check.code}
                        data-merchant-check-code={check.code}
                        data-merchant-check-status={check.status}
                    >
                        <CardHeader className="flex flex-row items-center justify-between space-y-0">
                            <CardTitle className="text-base">{check.title}</CardTitle>
                            <ChecklistCheckStatusBadge check={check} />
                        </CardHeader>
                        {check.status !== 'pass' ? (
                            <CardContent>
                                {check.details.length > 0 && check.issues.length === 0 ? (
                                    <div className="space-y-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="text-sm">{check.details[0]}</div>
                                            <ChecklistTopLevelAction
                                                check={check}
                                                onOpenMerchantBusinessDetails={
                                                    handleOpenMerchantBusinessDetails
                                                }
                                                onOpenMerchantConfiguration={
                                                    handleOpenMerchantConfiguration
                                                }
                                                onOpenOrganizationUsers={
                                                    handleOpenOrganizationUsers
                                                }
                                                onOpenPermissionsAudit={handleOpenPermissionsAudit}
                                            />
                                        </div>
                                        {check.details.length > 1 ? (
                                            <ul className="list-disc pl-5 space-y-1">
                                                {check.details.slice(1).map((detail) => (
                                                    <li key={detail} className="text-sm">
                                                        {detail}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </div>
                                ) : (
                                    <>
                                        {check.issues.length > 0 ? (
                                            <div className="space-y-3">
                                                {physicalTerminalIssueCounts !== null ? (
                                                    <div
                                                        className="flex justify-end gap-2"
                                                        data-physical-terminal-error-count={
                                                            physicalTerminalIssueCounts.errorCount
                                                        }
                                                        data-physical-terminal-warning-count={
                                                            physicalTerminalIssueCounts.warningCount
                                                        }
                                                    >
                                                        <Badge variant="destructive">
                                                            {`${physicalTerminalIssueCounts.errorCount} errors`}
                                                        </Badge>
                                                        <Badge className="border-transparent bg-yellow-100 text-yellow-800">
                                                            {`${physicalTerminalIssueCounts.warningCount} warnings`}
                                                        </Badge>
                                                    </div>
                                                ) : null}
                                                <div className="flex justify-end">
                                                    <ChecklistTopLevelAction
                                                        check={check}
                                                        onOpenMerchantBusinessDetails={
                                                            handleOpenMerchantBusinessDetails
                                                        }
                                                        onOpenMerchantConfiguration={
                                                            handleOpenMerchantConfiguration
                                                        }
                                                        onOpenOrganizationUsers={
                                                            handleOpenOrganizationUsers
                                                        }
                                                        onOpenPermissionsAudit={
                                                            handleOpenPermissionsAudit
                                                        }
                                                    />
                                                </div>
                                                {aggregatedTerminalQaIssues.map(
                                                    (aggregatedIssue) => (
                                                        <TerminalDeviceQaCard
                                                            key={getTerminalIssueGroupKey({
                                                                issue_type: 'terminal',
                                                                message: '',
                                                                terminal: aggregatedIssue.terminal,
                                                            })}
                                                            terminal={aggregatedIssue.terminal}
                                                            failedChecks={
                                                                aggregatedIssue.failedChecks
                                                            }
                                                            onOpenTerminalResource={
                                                                handleOpenTerminalResource
                                                            }
                                                            onOpenCredentialLink={
                                                                handleOpenCredentialLink
                                                            }
                                                        />
                                                    )
                                                )}
                                                {nonTerminalIssues.map((issue, issueIndex) => (
                                                    <ChecklistIssueCard
                                                        key={`${check.code}-${issue.issue_type}-${issueIndex}`}
                                                        issue={issue}
                                                        checkCode={checkCode}
                                                        organizationUuid={organizationUuid}
                                                        onViewUser={handleViewUser}
                                                        onReviewPermissionIssue={
                                                            handleReviewPermissionIssue
                                                        }
                                                        onViewCredential={handleViewCredential}
                                                        onEditCredential={handleEditCredential}
                                                        onDisableCredential={
                                                            handleDisableCredential
                                                        }
                                                        onEnableCredential={handleEnableCredential}
                                                        onDeleteCredential={handleDeleteCredential}
                                                        onDisconnectCredential={
                                                            handleDisconnectCredential
                                                        }
                                                        onOpenTerminalResource={
                                                            handleOpenTerminalResource
                                                        }
                                                        onOpenCredentialLink={
                                                            handleOpenCredentialLink
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        ) : check.details.length > 0 ? (
                                            <ul className="list-disc pl-5 space-y-1">
                                                {check.details.map((detail) => (
                                                    <li key={detail} className="text-sm">
                                                        {detail}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-sm text-muted-foreground">
                                                Check failed; no renderable issue records were
                                                returned.
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        ) : null}
                    </Card>
                );
            })}

            <TerminalCommandReadinessFleet
                organizationUuid={organizationUuid}
                onVerdictChange={handleTerminalCommandReadinessFleetVerdictChange}
            />

            <SecretModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                organizationUuid={organizationUuid}
                organizationName="Merchant organization"
                secret={selectedCredential ?? undefined}
                mode={modalMode}
            />

            <CredentialDeleteDialog
                open={deleteConfirmOpen}
                onOpenChange={(isOpen) => {
                    setDeleteConfirmOpen(isOpen);
                    if (!isOpen) {
                        setCredentialToDelete(null);
                        setDeleteConflictUsages(null);
                        setSelectedDeleteUsageKeys([]);
                    }
                }}
                credentialLabel={
                    credentialToDelete === null
                        ? ''
                        : `${getSystemName(credentialToDelete.system_id)} (${credentialToDelete.account_id})`
                }
                usages={deleteCredentialUsages}
                isLoadingUsages={isDeleteCredentialUsagesLoading}
                usageLoadError={deleteCredentialUsagesError}
                selectedUsageKeys={selectedDeleteUsageKeys}
                onToggleUsageSelection={handleToggleDeleteUsageSelection}
                onToggleSelectAll={handleToggleDeleteSelectAll}
                onConfirm={handleConfirmDeleteCredential}
                onRemove={handleRemoveDeleteUsages}
                isDeleting={deleteMutation.isPending}
                isRemoving={disconnectMutation.isPending}
            />

            <CredentialDisconnectDialog
                open={disconnectDialogOpen}
                onOpenChange={(isOpen) => {
                    setDisconnectDialogOpen(isOpen);
                    if (!isOpen) {
                        setCredentialToDisconnect(null);
                        setSelectedUsageKeys([]);
                    }
                }}
                usages={credentialUsagesResponse?.usages ?? []}
                isLoadingUsages={isCredentialUsagesLoading}
                usageLoadError={credentialUsagesError}
                selectedUsageKeys={selectedUsageKeys}
                onToggleUsageSelection={handleToggleUsageSelection}
                onConfirm={handleConfirmDisconnect}
                isDisconnecting={disconnectMutation.isPending}
            />
        </div>
    );
}
